
import { Dealer } from "@/types/dealer";
import { Euro, MapPin, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { 
  formatDistance, 
  calculateDistance, 
} from "@/lib/geolocation";
import { cn } from "@/lib/utils";

type DealersListProps = {
  dealers: Dealer[];
  userPosition: GeolocationPosition | null;
  onDealerSelect?: (dealer: Dealer) => void;
  className?: string;
};

export function DealersList({ dealers, userPosition, onDealerSelect, className }: DealersListProps) {
  if (!userPosition) {
    return (
      <p className="text-sm text-muted-foreground">
        Rileva la tua posizione per vedere i compro oro più vicini
      </p>
    );
  }
  
  // Sort dealers by distance from user
  const sortedDealers = [...dealers].sort((a, b) => {
    const lat = userPosition.coords.latitude;
    const lon = userPosition.coords.longitude;
    const distanceA = calculateDistance(lat, lon, a.latitude, a.longitude);
    const distanceB = calculateDistance(lat, lon, b.latitude, b.longitude);
    return distanceA - distanceB;
  });
  
  return (
    <div className={cn("space-y-2", className)}>
      {sortedDealers.map((dealer) => {
        const lat = userPosition.coords.latitude;
        const lon = userPosition.coords.longitude;
        const distance = formatDistance(
          calculateDistance(lat, lon, dealer.latitude, dealer.longitude)
        );
        
        return (
          <div 
            key={dealer.id}
            className="flex items-center justify-between p-3 border rounded-md hover:bg-accent/50 cursor-pointer transition-colors bg-background"
            onClick={() => onDealerSelect && onDealerSelect(dealer)}
          >
            <div className="flex flex-col">
              <div className="flex items-center">
                <span className="font-medium text-foreground">{dealer.name}</span>
                {dealer.activated && (
                  <Badge variant="outline" className="ml-2 text-xs text-green-600 border-green-300">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Attivato
                  </Badge>
                )}
              </div>
              <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                {dealer.address}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {dealer.buySpread !== undefined && (
                <Badge variant="outline" className="flex items-center text-foreground">
                  <Euro className="h-3 w-3 mr-1" />
                  {dealer.buySpread.toFixed(1)}%
                </Badge>
              )}
              <Badge variant="outline" className="text-foreground">{distance}</Badge>
            </div>
          </div>
        );
      })}
    </div>
  );
}
